import {useHistoryStoragesWithMaterialQuery} from '@domain/material-storage'
import {customThemeConstants} from '@hconnect/uikit/src/lib2'
import {useUpmPlantContext} from '@hconnect/upm'
import {dataTestId} from '@hproduce/suite-core'
import {Close} from '@mui/icons-material'
import {IconButton, Skeleton, Stack, useTheme} from '@mui/material'
import {toPixel, usePlanningChartStartEnd} from '@widgets/planning-schedule-chart'
import {SelectMaterialIdDropdown} from '@widgets/stock-card'
import React, {useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../../consts'
import {useSelectedOptimizerPlan} from '../../../providers'
import {Legend, LegendItem} from '../../Legend'
import {useSelectedMaterialId} from '../SelectedMaterialIdProvider'
import {useGetOptimizedMaterialIds} from '../utils/useGetOptimizedMaterialIds'

type OptimizerStockLevelsCardMaterialDetailHeaderProps = {
  isCollapsed: boolean
}

export const OptimizerStockLevelsCardMaterialDetailHeader: React.FC<
  OptimizerStockLevelsCardMaterialDetailHeaderProps
> = ({isCollapsed}) => {
  const {plantId: plantCode} = useUpmPlantContext()
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {spacing, palette} = useTheme()
  const {startOfPlan, endOfPlan} = usePlanningChartStartEnd()
  const {selectedInTableScheduleId} = useSelectedOptimizerPlan()
  const {data: storagesWithMaterials} = useHistoryStoragesWithMaterialQuery({
    plantId: plantCode,
    timeFrame: [startOfPlan, endOfPlan]
  })

  const optimizedMaterialIds = useGetOptimizedMaterialIds(plantCode)

  const isOptimizedPlanSelected = selectedInTableScheduleId !== undefined

  const isLoading = optimizedMaterialIds === undefined || storagesWithMaterials === undefined

  const {materialId, selectMaterialId} = useSelectedMaterialId()

  // filter only loaded material ids
  const filteredMaterialId =
    materialId && optimizedMaterialIds?.includes(materialId) ? materialId : undefined

  const unselectMaterial = useCallback(() => {
    selectMaterialId(undefined)
  }, [selectMaterialId])

  if (isLoading) {
    return <Skeleton height={toPixel(100)} variant="rectangular" />
  }

  return (
    <Stack direction="row" spacing={2} justifyContent="flex-end" alignItems="center">
      {!isCollapsed && (
        <Legend testId="silo_levels_legend">
          <LegendItem
            label={t('optimizer.selectedPlan')}
            indicator={{
              style: {backgroundColor: palette.primary.light},
              variant: 'circle'
            }}
          />
          {isOptimizedPlanSelected && (
            <LegendItem
              label={t('optimizer.currentPlan')}
              indicator={{
                style: {backgroundColor: customThemeConstants().palette.accent1},
                variant: 'circle'
              }}
            />
          )}
          <LegendItem label={t('optimizer.minLevel')}>
            <svg width={spacing(4)} height="3px">
              <line
                x1={0}
                x2={30}
                strokeWidth={1}
                y1={1}
                y2={1}
                stroke={palette.error.light}
                strokeDasharray="8,5"
              />
            </svg>
          </LegendItem>
        </Legend>
      )}
      <SelectMaterialIdDropdown
        selectedMaterialId={filteredMaterialId}
        changeSelectedMaterialId={selectMaterialId}
        materialIds={optimizedMaterialIds}
      />
      <div>
        <IconButton
          onClick={unselectMaterial}
          color="primary"
          {...dataTestId('close_material_detail_button')}
        >
          <Close />
        </IconButton>
      </div>
    </Stack>
  )
}
